import { useEffect, useState } from 'react';
import { facebookAuthorize, listFacebookPages } from '../../api/endpoints';
import type { FacebookAuthorizeResult, FacebookLoginConfig, FacebookPageOption } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { FacebookIcon, InstagramIcon } from '../../lib/brandIcons';
import { runFacebookLogin } from '../../lib/facebookLogin';
import { setupFacebookSdk } from '../../lib/whatsappSignup';

type Phase = 'idle' | 'popup' | 'listing' | 'pick' | 'connecting';

/**
 * "Continue with Facebook" for Messenger: the dialog, then a picker, because
 * one account routinely manages several Pages and choosing one is the
 * interactive step. The Page's linked Instagram account is offered beside it —
 * same token, one more tick — so an operator whose Instagram is already
 * attached to their Page never needs a second sign-in for it.
 *
 * The user token the dialog resolves is held here for the length of the
 * picker and posted twice (to list, to connect); it is never stored anywhere.
 */
export function FacebookPages({
  config,
  onDone,
}: {
  config: FacebookLoginConfig;
  onDone: (result: FacebookAuthorizeResult) => void;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [pages, setPages] = useState<FacebookPageOption[]>([]);
  const [pageId, setPageId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [withInstagram, setWithInstagram] = useState(true);
  const [instagramName, setInstagramName] = useState('');

  // Warm the FB SDK while the admin reads the page, so the click handler opens
  // the popup immediately — a popup opened late trips blockers.
  useEffect(() => {
    void setupFacebookSdk(config).catch(() => {
      // Ignored: runFacebookLogin retries the load and surfaces the error.
    });
  }, [config]);

  const start = async () => {
    setError(null);
    setPhase('popup');
    try {
      const result = await runFacebookLogin(config);
      if (result.status === 'cancelled') {
        setPhase('idle'); // closed the popup — not an error
        return;
      }
      setPhase('listing');
      const list = await listFacebookPages(result.userAccessToken);
      setToken(result.userAccessToken);
      setPages(list);
      const first = list.find((page) => !page.connected);
      setPageId(first?.id ?? null);
      setPhase('pick');
    } catch (e) {
      setPhase('idle');
      setError(e instanceof Error ? e.message : 'The Facebook sign-in could not be completed.');
    }
  };

  const selected = pages.find((page) => page.id === pageId) ?? null;
  const instagramOffered = !!selected?.instagram && !selected.instagramConnected;

  const connect = async () => {
    if (!token || !selected) return;
    setError(null);
    setPhase('connecting');
    try {
      const result = await facebookAuthorize({
        userAccessToken: token,
        pageId: selected.id,
        ...(name.trim() ? { name: name.trim() } : {}),
        instagram: instagramOffered && withInstagram,
        ...(instagramName.trim() ? { instagramName: instagramName.trim() } : {}),
      });
      onDone(result);
    } catch (e) {
      setPhase('pick');
      setError(e instanceof Error ? e.message : 'The Page could not be connected.');
    }
  };

  if (phase === 'listing' || phase === 'connecting') {
    return (
      <div className="card card-pad connect-panel">
        <Spinner large />
        <p>
          <strong>{phase === 'listing' ? 'Reading your Pages…' : 'Finishing the connection…'}</strong>
        </p>
        <p className="connect-panel-hint">
          {phase === 'listing'
            ? 'Asking Facebook which Pages the account you signed in with manages.'
            : 'Subscribing the Page to Messenger events and creating the inbox — this takes a few seconds.'}
        </p>
      </div>
    );
  }

  if (phase === 'pick') {
    const selectable = pages.filter((page) => !page.connected);
    return (
      <div className="card card-pad connect-form fb-pages">
        {error && <ErrorNote error={error} />}
        {!pages.length ? (
          <Note tone="warn">
            The Facebook account you signed in with manages no Pages. Sign in with an account that is an
            admin of the Page you want to connect.
          </Note>
        ) : !selectable.length ? (
          <Note tone="info">Every Page on that account is already connected here.</Note>
        ) : (
          <>
            <p className="wa-embed-copy">Pick the Page whose Messenger conversations should land here.</p>
            <div className="fb-page-list" role="radiogroup" aria-label="Facebook Pages">
              {pages.map((page) => (
                <label key={page.id} className={`fb-page-row${page.connected ? ' fb-page-row-taken' : ''}`}>
                  <input
                    type="radio"
                    name="fb-page"
                    value={page.id}
                    checked={pageId === page.id}
                    disabled={page.connected}
                    onChange={() => setPageId(page.id)}
                  />
                  {page.pictureUrl ? (
                    <img className="fb-page-pic" src={page.pictureUrl} alt="" />
                  ) : (
                    <span className="fb-page-pic fb-page-pic-empty">
                      <FacebookIcon size={14} />
                    </span>
                  )}
                  <span className="fb-page-text">
                    <span className="fb-page-name">{page.name}</span>
                    <span className="fb-page-sub">
                      {page.connected
                        ? 'Already connected'
                        : page.instagram
                          ? `Instagram @${page.instagram.username ?? page.instagram.id}${page.instagramConnected ? ' (already connected)' : ''}`
                          : 'No Instagram account linked'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <Field label="Inbox name (optional)" hint="Defaults to the Page name.">
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={selected ? `${selected.name} Messenger` : 'Messenger'}
              />
            </Field>
            {instagramOffered && selected?.instagram && (
              <div className="fb-page-instagram">
                <label className="fb-page-check">
                  <input
                    type="checkbox"
                    checked={withInstagram}
                    onChange={(e) => setWithInstagram(e.target.checked)}
                  />
                  <InstagramIcon size={14} />
                  <span>
                    Also connect <strong>@{selected.instagram.username ?? selected.instagram.id}</strong> as an
                    Instagram inbox — same sign-in, its DMs arrive beside Messenger.
                  </span>
                </label>
                {withInstagram && (
                  <Field label="Instagram inbox name (optional)" hint="Defaults to the @handle.">
                    <input
                      className="input"
                      value={instagramName}
                      onChange={(e) => setInstagramName(e.target.value)}
                      placeholder={`@${selected.instagram.username ?? selected.instagram.id} Instagram`}
                    />
                  </Field>
                )}
              </div>
            )}
            <div className="connect-form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setPhase('idle')}>
                Start over
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void connect()} disabled={!selected}>
                Connect {selected ? selected.name : 'Page'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="card card-pad connect-form wa-embed">
      {error && <ErrorNote error={error} />}
      <p className="wa-embed-copy">
        Sign in with a Facebook account that administers the Page. Facebook asks you to grant access to
        the Pages you choose; you pick one here, and its Messenger conversations become an inbox. If an
        Instagram professional account is linked to the Page, it can come along on the same sign-in.
      </p>
      <div className="connect-form-actions wa-embed-actions">
        <button
          type="button"
          className="btn btn-primary wa-embed-btn"
          onClick={() => void start()}
          disabled={phase === 'popup'}
        >
          {phase === 'popup' ? <Spinner /> : <FacebookIcon size={15} tint="currentColor" />}
          {phase === 'popup' ? 'Waiting for the Facebook window…' : 'Continue with Facebook'}
        </button>
      </div>
    </div>
  );
}
